// Single source for the public site's SEO: per-page head data (used by the
// build-time prerender and by usePageMeta at runtime) and JSON-LD builders
// (rendered into the pages, so the prerendered HTML carries them too).
import { STORE_LINKS, SOCIAL_LINKS } from 'views/home-components/homeContent';

export const SITE_URL = 'https://okumaplatformu.com';
export const SITE_NAME = 'Okuma Platformu';
export const OG_IMAGE = `${SITE_URL}/og-image.png`;
export const LOGO_URL = `${SITE_URL}/logo-512.png`;

// index: false keeps a page out of search results (still crawlable).
export const PAGE_META = {
  '/': {
    title: 'Okuma Platformu | Okul öncesi ve ilkokul için dijital okuma',
    description:
      "Okul öncesi ve ilkokul öğrencileri için 13 seviyeli Okuma Ağacı serisinden 200+ sesli, resimli hikâye; Ses Bilgisi modülü ve öğretmenler için görev, materyal ve rapor araçları. Web, iOS ve Android'de.",
    index: true,
    priority: '1.0',
  },
  '/buy-now': {
    title: 'Okulunuz için teklif alın | Okuma Platformu',
    description:
      'Okuma Platformu’nu okulunuzda kullanmak için teklif isteyin: Okuma Ağacı kütüphanesi, Ses Bilgisi modülü, öğretmen paneli ve mobil uygulamalar.',
    index: true,
    priority: '0.8',
  },
  '/contact': {
    title: 'İletişim | Okuma Platformu',
    description:
      'Okuma Platformu ile ilgili sorularınız için bize ulaşın. Merkez ofis İstanbul, AR-GE şubesi Malatya Teknokent.',
    index: true,
    priority: '0.6',
  },
  '/signin': {
    title: 'Giriş Yap | Okuma Platformu',
    description:
      'Okuma Platformu öğrenci ve öğretmen girişi. Okulunuzun verdiği kullanıcı adı ve şifreyle giriş yapın.',
    index: true,
    priority: '0.5',
  },
  '/forgot-password': {
    title: 'Şifremi Unuttum | Okuma Platformu',
    description: 'Okuma Platformu hesabınız için yeni şifre oluşturma bağlantısı isteyin.',
    index: false,
  },
  '/create-password': {
    title: 'Yeni Şifre | Okuma Platformu',
    description: 'Okuma Platformu hesabınız için yeni şifrenizi belirleyin.',
    index: false,
  },
  '/kvkk': {
    title: 'KVKK Aydınlatma Metni | Okuma Platformu',
    description: 'Okuma Platformu kişisel verilerin korunması aydınlatma metni.',
    index: true,
    priority: '0.3',
  },
  '/terms-of-use': {
    title: 'Kullanım Koşulları | Okuma Platformu',
    description: 'Okuma Platformu kullanım koşulları.',
    index: true,
    priority: '0.3',
  },
};

export const ORGANIZATION_ID = `${SITE_URL}/#organization`;

export const organizationLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': ORGANIZATION_ID,
  name: SITE_NAME,
  url: `${SITE_URL}/`,
  logo: LOGO_URL,
  sameAs: SOCIAL_LINKS.map((social) => social.href),
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    url: `${SITE_URL}/contact`,
    availableLanguage: 'Turkish',
  },
  parentOrganization: {
    '@type': 'Organization',
    name: 'Edict Digital Bilgi Teknolojileri A.Ş.',
    url: 'https://www.edictdigital.com/',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Kavacık Mah. Gülşen Sk. No: 12/3',
      addressLocality: 'Beykoz',
      addressRegion: 'İstanbul',
      addressCountry: 'TR',
    },
  },
});

export const websiteLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  name: SITE_NAME,
  url: `${SITE_URL}/`,
  inLanguage: 'tr-TR',
  publisher: { '@id': ORGANIZATION_ID },
});

const audience = {
  '@type': 'EducationalAudience',
  educationalRole: 'student',
  audienceType: 'Okul öncesi ve ilkokul öğrencileri',
};

export const appsLd = () => [
  {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'Web',
    inLanguage: 'tr',
    audience,
    publisher: { '@id': ORGANIZATION_ID },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'MobileApplication',
    name: 'Okuma Platformu Okul',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'iOS, Android',
    inLanguage: 'tr',
    audience,
    installUrl: [STORE_LINKS.okul.appStore, STORE_LINKS.okul.googlePlay],
    publisher: { '@id': ORGANIZATION_ID },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'MobileApplication',
    name: 'Okuma Platformu Bireysel',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'iOS, Android',
    inLanguage: 'tr',
    audience,
    installUrl: [STORE_LINKS.bireysel.appStore, STORE_LINKS.bireysel.googlePlay],
    publisher: { '@id': ORGANIZATION_ID },
  },
];

export const faqLd = (faq) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faq.map((item) => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: { '@type': 'Answer', text: item.a },
  })),
});
